import * as React from 'react';
import cn from 'classnames';
import { Copy } from 'src/common/icons';
import { useAlerts } from 'src/common/AlertManager';
import styles from './styles.module.scss';

/**
 * Small pieces shared across the migration settings page.
 *
 * The first version of this screen was six large cards of prose with form
 * fields buried inside them, and it read as documentation rather than as
 * settings. These are the pieces that put it back in the house style: a label
 * on the left, a control on the right, one short line of help underneath.
 */

/** One setting: label and help on the left, the control on the right.
 *
 *  Matches the `fieldRow` shape used by the AI and Storage settings pages, so
 *  this screen sits in the same visual family as the rest of Settings. */
export const Row: React.FC<{
  label: string;
  help?: React.ReactNode;
  /** Stacks the control under the label instead of beside it. For a control
   *  that needs the full width (a URL, a long value), where squeezing it into
   *  the right-hand column would truncate the thing being read. */
  stacked?: boolean;
  /** For a control with a natural size of its own — a toggle, a checkbox, a
   *  status pill. Those hug the right-hand edge instead of being stretched
   *  across the control column, which would leave a 42px switch floating in the
   *  middle of a 22rem box. */
  compact?: boolean;
  className?: string;
}> = ({ label, help, stacked, compact, className, children }) => (
  <div
    className={cn(
      styles.row,
      { [styles.rowStacked]: stacked, [styles.rowCompact]: compact },
      className,
    )}
  >
    <div className={styles.rowLabel}>
      <span className={styles.rowLabelText}>{label}</span>
      {help && <span className={styles.rowHelp}>{help}</span>}
    </div>
    <div className={styles.rowControl}>{children}</div>
  </div>
);

type PillTone = 'neutral' | 'good' | 'warn' | 'bad' | 'busy';

export const Pill: React.FC<{ tone?: PillTone; className?: string }> = ({
  tone = 'neutral',
  className,
  children,
}) => <span className={cn(styles.pill, styles[`pill_${tone}`], className)}>{children}</span>;

/** A short message. `warn` is reserved for the cases where getting it wrong has
 *  a consequence the customer cannot undo on their own. */
export const Note: React.FC<{ tone?: 'info' | 'warn' | 'good'; className?: string }> = ({
  tone = 'info',
  className,
  children,
}) => <div className={cn(styles.note, styles[`note_${tone}`], className)}>{children}</div>;

/** A labelled number, optionally a filter.
 *
 *  With `onClick` it renders as a button, because a percentage is only half an
 *  answer: "17% will not work" needs a next click that says WHICH ones. */
export const Stat: React.FC<{
  value: React.ReactNode;
  label: string;
  tone?: 'neutral' | 'good' | 'warn' | 'bad';
  hint?: string;
  onClick?: () => void;
  selected?: boolean;
}> = ({ value, label, tone = 'neutral', hint, onClick, selected }) => {
  const body = (
    <>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statLabel}>{label}</div>
    </>
  );
  const className = cn(styles.stat, styles[`stat_${tone}`], {
    [styles.statClickable]: !!onClick,
    [styles.statSelected]: selected,
  });

  if (!onClick) {
    return (
      <div className={className} title={hint}>
        {body}
      </div>
    );
  }
  return (
    <button type="button" className={className} title={hint} onClick={onClick} aria-pressed={selected}>
      {body}
    </button>
  );
};

/** A value the customer has to paste somewhere else, with a copy button.
 *  Everything shown this way is meant to be copied exactly — a hostname, a
 *  one-time code — so selecting it by hand is the failure worth removing. */
export const CopyableValue: React.FC<{ value: string; className?: string }> = ({ value, className }) => {
  const { showSuccess, showAlert } = useAlerts();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      showSuccess('Copied');
    } catch {
      showAlert('Could not copy — select the text and copy it manually');
    }
  };

  return (
    <div className={cn(styles.copyable, className)}>
      <code className={styles.copyableValue}>{value}</code>
      <button type="button" className={styles.copyButton} onClick={copy} aria-label={`Copy ${value}`}>
        <Copy />
      </button>
    </div>
  );
};

/**
 * The title and one-line explanation at the top of a dialog.
 *
 * Checking your URLs and copying your library are things you *do*, once, not
 * settings you keep — so they are dialogs, the same as adding a schema or an
 * analytics destination. An earlier version opened them as panels below the
 * button, which pushed the page around and blurred the line between "a form I
 * am filling in" and "a setting that is saved".
 */
export const ModalHeader: React.FC<{ title: string; lead?: string }> = ({ title, lead }) => (
  <header className={styles.modalHeader}>
    <h2 className={styles.modalTitle}>{title}</h2>
    {lead && <p className={styles.sectionLead}>{lead}</p>}
  </header>
);

/** A proportional bar broken into named segments — used where the ratio is the
 *  point rather than the values ("94% of your URLs are unaffected"). */
export const SegmentBar: React.FC<{
  segments: { value: number; tone: 'good' | 'warn' | 'bad' | 'neutral'; label: string }[];
  className?: string;
}> = ({ segments, className }) => {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  if (!total) return null;
  return (
    <div
      className={cn(styles.segmentBar, className)}
      role="img"
      aria-label={segments.map((s) => `${s.value} ${s.label}`).join(', ')}
    >
      {segments
        .filter((s) => s.value > 0)
        .map((s) => (
          <span
            key={s.label}
            className={cn(styles.segment, styles[`segment_${s.tone}`])}
            style={{ width: `${(100 * s.value) / total}%` }}
            title={`${s.label}: ${s.value}`}
          />
        ))}
    </div>
  );
};

/**
 * A link that downloads a small example of the file an upload control expects.
 *
 * "Upload a .txt or .csv" is not an instruction anybody can follow without
 * knowing which column we read. Showing the format in help text still leaves
 * the reader to build the file; handing them a working one to open and edit
 * removes the guess entirely.
 */
export const ExampleFileLink: React.FC<{ filename: string; contents: string; label?: string }> = ({
  filename,
  contents,
  label = 'Download an example file',
}) => {
  const download = () => {
    const blob = new Blob([contents], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <button type="button" className={styles.linkButton} onClick={download}>
      {label}
    </button>
  );
};

/** Relative time. Absolute timestamps read as noise on a page about progress. */
export function timeAgo(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Pull a readable sentence out of whatever the API returned.
 *
 *  Two shapes reach here: migration-onramp's `{error: "..."}` — written for the customer
 *  and worth showing word for word — and the SaaS backend's `{message: "..."}`.
 *  The generic fallback is a last resort, because "something went wrong" is the
 *  message that generates a support ticket. */
export function errorText(err: unknown, fallback: string): string {
  const e = err as { error?: any; message?: string } | undefined;
  const body = e?.error;
  if (typeof body === 'string' && body.trim()) return body;
  if (body && typeof body === 'object') {
    if (typeof body.error === 'string' && body.error.trim()) return body.error;
    if (typeof body.message === 'string' && body.message.trim()) return body.message;
  }
  if (typeof e?.message === 'string' && e.message.trim() && !e.message.startsWith('Request to ')) {
    return e.message;
  }
  return fallback;
}
